/**
 * Sprint 207X+ — Milestone celebration toast.
 *
 * Mounted inside ConversationsChat. Polls /api/teen/milestones every 25s
 * while chat is open and tracks the set of run_ids already acknowledged
 * in localStorage. When a brand-new completed milestone appears (i.e.
 * the teen flipped a challenge to `completed` mid-conversation), the
 * toast pops with a CSS-only confetti burst and a "Share to my Brave
 * Wall" button that opens the existing SharePracticeModal.
 *
 * Pure additive — does not interfere with chat send/receive or LLM flow.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Trophy, X, Share2, Sparkles } from 'lucide-react';
import { apiClient } from '../../api/client';

const SEEN_KEY = 'hibff_seen_milestone_runs';

const loadSeen = () => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
};
const saveSeen = (set) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-200))); } catch {}
};

// Confetti — 28 CSS-only spans with random hues + transform deltas.
const ConfettiBurst = () => {
  const pieces = React.useMemo(() => Array.from({ length: 28 }, (_, i) => {
    const hue = Math.floor(Math.random() * 360);
    const dx = (Math.random() * 280 - 140).toFixed(0);
    const dy = (Math.random() * -240 - 40).toFixed(0);
    const rot = (Math.random() * 720 - 360).toFixed(0);
    const delay = (Math.random() * 120).toFixed(0);
    const size = 6 + Math.floor(Math.random() * 6);
    return { i, hue, dx, dy, rot, delay, size };
  }), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-visible">
      <style>{`
        @keyframes hibff-confetti-fly {
          0%   { transform: translate(0,0) rotate(0deg); opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translate(var(--dx,0px), var(--dy,0px)) rotate(var(--rot,0deg)); opacity: 0; }
        }
        .hibff-confetti-piece {
          position: absolute;
          left: 50%; top: 60%;
          border-radius: 2px;
          animation: hibff-confetti-fly 1.4s cubic-bezier(.22,.61,.36,1) forwards;
        }
      `}</style>
      {pieces.map(p => (
        <span
          key={p.i}
          className="hibff-confetti-piece"
          style={{
            width: p.size, height: p.size,
            background: `hsl(${p.hue}, 90%, 60%)`,
            '--dx': `${p.dx}px`, '--dy': `${p.dy}px`, '--rot': `${p.rot}deg`,
            animationDelay: `${p.delay}ms`,
          }}
        />
      ))}
    </div>
  );
};

const MilestoneCelebrationToast = ({ onShare, onMilestone }) => {
  // Keep the latest onMilestone in a ref so an inline-arrow prop doesn't
  // recreate celebrate() and tear down / re-open the SSE stream every render.
  const onMilestoneRef = useRef(onMilestone);
  useEffect(() => { onMilestoneRef.current = onMilestone; });
  const [celebrateState, setCelebrateState] = useState(null); // { run_id, title, sequence } | null
  const seenRef = useRef(loadSeen());
  const dismissTimerRef = useRef(null);

  // Internal helper shared by SSE + poll paths — declared first so `poll`
  // can safely include it in its dependency array without a forward ref.
  const celebrate = useCallback((event) => {
    if (!event || !event.run_id) return;
    if (seenRef.current.has(event.run_id)) return;
    seenRef.current.add(event.run_id);
    saveSeen(seenRef.current);
    setCelebrateState(event);
    if (onMilestoneRef.current) onMilestoneRef.current(event);
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = setTimeout(() => setCelebrateState(null), 7000);
  }, []);

  const poll = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/teen/milestones');
      const rows = data.rows || [];
      if (!rows.length) return;
      // First-mount: prime seen-set with everything we already know so we
      // don't immediately fire a stale celebration on page load.
      if (seenRef.current.size === 0) {
        rows.forEach(r => seenRef.current.add(r.run_id));
        saveSeen(seenRef.current);
        return;
      }
      const fresh = rows.find(r => r.run_id && !seenRef.current.has(r.run_id));
      if (fresh) {
        celebrate(fresh);
      }
    } catch { /* silent — non-teens or auth blips */ }
  }, [celebrate]);

  useEffect(() => {
    // First poll primes the seen-set; subsequent polls actually celebrate.
    poll();

    // Sprint 207X+ — open an SSE stream for <200ms latency. EventSource
    // can't send Authorization headers, so we pass the token as a query
    // param (matches FastAPI endpoint signature).
    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    let es = null;
    if (token && typeof EventSource !== 'undefined') {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      try {
        es = new EventSource(`${apiUrl}/api/teen/milestones/stream?token=${encodeURIComponent(token)}`);
        es.addEventListener('milestone_completed', (e) => {
          try {
            const data = JSON.parse(e.data);
            celebrate(data);
          } catch { /* ignore parse errors */ }
        });
        // 'ready' and 'error' are observed but no-op for the UI.
      } catch { /* SSE unavailable — poll fallback covers it */ }
    }

    // Background poll every 25s as a fallback in case the SSE channel
    // drops (mobile background, proxy timeout, etc.).
    const id = setInterval(poll, 25000);
    return () => {
      clearInterval(id);
      if (es) { try { es.close(); } catch {} }
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [poll, celebrate]);

  if (!celebrateState) return null;

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] w-[min(92vw,420px)]"
      data-testid="milestone-celebration-toast"
      role="alert"
    >
      <div className="relative bg-gradient-to-br from-amber-50 via-white to-pink-50 border-2 border-amber-300 rounded-2xl shadow-2xl px-5 py-4 overflow-visible">
        <ConfettiBurst />
        <button
          onClick={() => setCelebrateState(null)}
          className="absolute top-2 right-2 p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Dismiss celebration"
          data-testid="milestone-celebration-dismiss"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-pink-400 flex items-center justify-center flex-shrink-0 shadow-md">
            <Trophy size={22} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700 flex items-center gap-1">
              <Sparkles size={11} /> Milestone unlocked
            </p>
            <p className="text-sm font-bold text-gray-900 leading-tight mt-0.5" data-testid="milestone-celebration-title">
              {celebrateState.title}
              {celebrateState.sequence != null && (
                <span className="ml-1.5 text-xs font-semibold text-amber-700">#{celebrateState.sequence}</span>
              )}
            </p>
            <p className="text-xs text-gray-600 mt-1">Nice work — your courage just compounded.</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            onClick={() => setCelebrateState(null)}
            className="text-xs font-semibold text-gray-500 hover:text-gray-700 px-2 py-1.5"
            data-testid="milestone-celebration-later"
          >
            Later
          </button>
          <button
            onClick={() => {
              const fired = celebrateState;
              setCelebrateState(null);
              if (onShare) onShare(fired);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-md transition-shadow"
            data-testid="milestone-celebration-share"
          >
            <Share2 size={12} /> Share to my Brave Wall
          </button>
        </div>
      </div>
    </div>
  );
};

export default MilestoneCelebrationToast;
